import {
	FindPersonContactMode,
	findPersonOverrideRuleProblem,
	FindPersonProfileProblem,
	findPersonProfileProblem,
	MAX_PEOPLE_PER_COMPANY,
	MIN_PEOPLE_PER_COMPANY,
	usesFindPersonWaterfall,
} from "@signalist/shared-types";

import { ApolloType } from "@/enum/apollo.enum";
import { ListSourceType } from "@/enum/list.enum";
import {
	BulkFindPersonProfileSource,
	BulkFindPersonRequest,
	BulkFindPersonSelection,
	BulkFindPersonSkippedRow,
} from "@/pages/Private/redux/list/list-find-person.api";

import { CountrySelection, IcpPosition } from "../../pages/ListEditDetails/enum/icp.enum";
import { EnrichmentFilterConfig, Icp } from "../../pages/ListEditDetails/ListEditDetails";
import { List } from "../../schema/list";

/** The three search profiles of a Find person config, in the order the search tries them. */
export const PROFILE_SLOTS = ["first", "second", "third"] as const;

export type ProfileSlot = (typeof PROFILE_SLOTS)[number];

/** People per company a bulk find person can ask for: the shared bounds the API accepts. */
export { MAX_PEOPLE_PER_COMPANY, MIN_PEOPLE_PER_COMPANY };

/** What the list step shows (and list processing uses) for a list that never set the count. */
const LIST_DEFAULT_PEOPLE_PER_COMPANY = 3;

/** A tracker finds one person per company (PREC-4399), so its dialog starts there. */
const TRACKER_DEFAULT_PEOPLE_PER_COMPANY = 1;

export interface FindPeopleSeed {
	/** The list's own search profiles, a copy to edit for this run only; undefined when it has none. */
	config?: EnrichmentFilterConfig;
	peoplePerCompany: number;
	/** Whether the list has a search profile of its own to start from. */
	hasListProfiles: boolean;
	/**
	 * The list's Find person mode (PREC-3899), which the run may change; undefined on a list that
	 * does not find people at its companies, where the dialog offers no mode and sends none.
	 */
	contactMode?: FindPersonContactMode;
}

/** A count clamped to what the search accepts; anything that is not a number is the minimum. */
export function clampPeoplePerCompany(count: number | null | undefined): number {
	const whole = Math.floor(Number(count));

	return Number.isFinite(whole)
		? Math.min(MAX_PEOPLE_PER_COMPANY, Math.max(MIN_PEOPLE_PER_COMPANY, whole))
		: MIN_PEOPLE_PER_COMPANY;
}

/** Whether a config holds a search profile to search with, as the API decides it. */
export function hasSearchProfile(config: EnrichmentFilterConfig | undefined): boolean {
	return PROFILE_SLOTS.some((slot) => !!config?.[slot]);
}

/**
 * Where the dialog starts (PREC-4398): the list's own search profiles, from its source search
 * where the Find person step saves them (the API reads the same place), and its people per
 * company, or what the step shows when it never set one.
 */
export function seedFromList(list: List): FindPeopleSeed {
	const source = list.apollos?.find((apollo) => apollo.type === ApolloType.SOURCE)?.config as
		| { enrichmentFilterConfig?: EnrichmentFilterConfig; maxPerCompany?: number }
		| undefined;
	const config = source?.enrichmentFilterConfig
		? (JSON.parse(JSON.stringify(source.enrichmentFilterConfig)) as EnrichmentFilterConfig)
		: undefined;
	const fallback =
		list.sourceType === ListSourceType.TRACKER
			? TRACKER_DEFAULT_PEOPLE_PER_COMPANY
			: LIST_DEFAULT_PEOPLE_PER_COMPANY;

	return {
		config: hasSearchProfile(config) ? config : undefined,
		peoplePerCompany: clampPeoplePerCompany(source?.maxPerCompany ?? fallback),
		hasListProfiles: hasSearchProfile(config),
		contactMode: usesFindPersonWaterfall(list)
			? list.findPersonContactMode === FindPersonContactMode.NEW_PEOPLE_ONLY
				? FindPersonContactMode.NEW_PEOPLE_ONLY
				: FindPersonContactMode.CONTACTS_FIRST
			: undefined,
	};
}

/**
 * What a set profile still needs before it can be searched with, or null: the rule the API
 * refuses a profile by (`findPersonProfileProblem`, shared), stated over the profile itself,
 * since the step editor's own `error` flag is written only while that profile's editor is open.
 */
export function profileProblemOf(profile: Icp | undefined): FindPersonProfileProblem | null {
	return profile ? findPersonProfileProblem(profile) : null;
}

/** Why the profiles cannot be searched with as they are, or null when they can. */
export type ProfilesProblem =
	| { kind: "NO_PROFILE" }
	| { kind: "INCOMPLETE_PROFILE" }
	| { kind: "INCOMPLETE_RULE"; rule: number; problem: FindPersonProfileProblem };

export function profilesProblem(
	config: EnrichmentFilterConfig | undefined
): ProfilesProblem | null {
	if (!hasSearchProfile(config)) {
		return { kind: "NO_PROFILE" };
	}

	if (PROFILE_SLOTS.some((slot) => profileProblemOf(config?.[slot]))) {
		return { kind: "INCOMPLETE_PROFILE" };
	}

	for (const [index, rule] of (config?.overrideRules ?? []).entries()) {
		const problem = findPersonOverrideRuleProblem(rule);

		if (problem) {
			return { kind: "INCOMPLETE_RULE", rule: index + 1, problem };
		}
	}

	return null;
}

/** A new profile, as the Find person step starts its first one: CEO and similar, anywhere. */
export function newProfile(): Icp {
	return { position: IcpPosition.CEO_AND_SIMILAR };
}

/**
 * The search request: the selection, the profiles the dialog shows, sent inline so the run uses
 * exactly what the user saw and edited, whichever list or preset they started from, and the key of
 * the plan the user confirmed, so the search runs only what was quoted.
 */
export function buildFindPeopleRequest(
	selection: BulkFindPersonSelection,
	config: EnrichmentFilterConfig,
	peoplePerCompany: number,
	planKey: string,
	contactMode?: FindPersonContactMode
): BulkFindPersonRequest {
	return {
		...selection,
		searchProfile: {
			source: BulkFindPersonProfileSource.INLINE,
			enrichmentFilterConfig: config as Record<string, unknown>,
		},
		peoplePerCompany: clampPeoplePerCompany(peoplePerCompany),
		...(contactMode ? { findPersonContactMode: contactMode } : {}),
		planKey,
	};
}

/** The skipped rows grouped by their reason, most frequent first, for the dialog to list. */
export function groupSkippedRows(
	rows: BulkFindPersonSkippedRow[] | undefined
): { reason: string; count: number }[] {
	const counts = new Map<string, number>();

	for (const { reason } of rows ?? []) {
		counts.set(reason, (counts.get(reason) ?? 0) + 1);
	}

	return [...counts.entries()]
		.map(([reason, count]) => ({ reason, count }))
		.sort((a, b) => b.count - a.count);
}

/**
 * How a profile's personal location reads: anywhere, the company's HQ country, or the places
 * named. A region's countries are stored beside it, so a profile with regions is named by them.
 */
export function locationOf(
	profile: Icp
): { kind: "ANY" } | { kind: "HQ" } | { kind: "CUSTOM"; places: string[] } {
	if (profile.countrySelection === CountrySelection.HQ) {
		return { kind: "HQ" };
	}

	if (profile.countrySelection === CountrySelection.CUSTOM) {
		const regions = profile.regions ?? [];

		return { kind: "CUSTOM", places: regions.length > 0 ? regions : (profile.countries ?? []) };
	}

	return { kind: "ANY" };
}
